import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { getCookie } from "../../Utils/cookies";
import {
  FaEdit,
  FaFilePdf,
  FaCheck,
  FaTimes,
  FaArrowUp,
  FaSpinner,
  FaDownload,
  FaSave,
  FaBan,
  FaSearch,
  FaFilter,
  FaFileExcel,
  FaUpload,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

const CompleteInstallationMaharastra = () => {
  const empData = getCookie("empData") ? JSON.parse(getCookie("empData")) : {};
  const [installationData, setInstallationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedState, setSelectedState] = useState("Maharashtra");
  const [selectedRemarks, setSelectedRemarks] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchInput, setDebouncedSearchInput] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [selectedImage, setSelectedImage] = useState({
    url: null,
    fieldName: null,
    saralId: null,
    date: null,
  });
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    latitude: "",
    longitude: "",
    remarks: "",
  });

  const [filterData] = useState([
    { remarks: "Earthing Issue" },
    { remarks: "Water Flow - Less Water" },
    { remarks: "Motor Not Working" },
    { remarks: "Pump Jammed" },
    { remarks: "Structure Issue" },
    { remarks: "Plate Crack" },
    { remarks: "Anti Theft Nut Not Broken" },
    { remarks: "Foundation Payment Not Received" },
    { remarks: "Lightning Arrester - Earthing Issue" },
    { remarks: "Connector Issue" },
    { remarks: "Controller Issue" },
    { remarks: "Head Change" },
    { remarks: "Not Connected With Mobile" },
    { remarks: "Phase Loss Fault" },
    { remarks: "LA Spike" },
  ]);

  const [imageUploads, setImageUploads] = useState({});
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [decliningId, setDecliningId] = useState(null);
  const [declineModal, setDeclineModal] = useState({
    open: false,
    installationId: null,
    photos: [],
  });
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [excelUploadModal, setExcelUploadModal] = useState(false);
  const [selectedExcelFile, setSelectedExcelFile] = useState(null);
  const [bulkApproveResult, setBulkApproveResult] = useState(null);

  const getFullImageUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${process.env.REACT_APP_BASE_URL_API_URL}${url.startsWith("/") ? url : "/" + url}`;
  };

  const states = ["Maharashtra", "Haryana", "Punjab", "Rajasthan", "Chhattisgarh"];

  const isRejectedByVT1 = (installation) =>
    installation.status === "Rejected By VT-1" || installation.stageId?.stage === "Rejected By VT-1";
  const isApprovedByVT2 = (installation) => installation.stageId?.stage === "Approved By VT-2";
  const getDisplayStatus = (installation) =>
    installation.stageId?.stage ||
    (installation.status === "approved"
      ? "Approved By VT-2"
      : installation.status === "declined"
      ? "Rejected By VT-2"
      : installation.status || "Pending");
  const getStatusColor = (status) => {
    if (status === "Approved By VT-2") return "bg-green-100 text-green-800";
    if (status === "Rejected By VT-2") return "bg-red-100 text-red-800";
    if (status === "Rejected By VT-1") return "bg-orange-100 text-orange-800";
    return "bg-gray-100 text-gray-800";
  };

  // Fetch data with server-side pagination & filtering
  const fetchInstallationData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const department = empData?.department || "";
      const params = new URLSearchParams();
      params.append("state", selectedState);
      params.append("department", department);
      params.append("page", currentPage);
      params.append("limit", limit);
      // Use "search" parameter as shown in the API docs
      if (debouncedSearchInput.trim()) params.append("search", debouncedSearchInput.trim());
      if (selectedRemarks) params.append("remarks", selectedRemarks);

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL_API_URL}/service-team/get-new-installation-data?${params.toString()}`,
        { timeout: 30000, headers: { "Content-Type": "application/json" } }
      );
      if (response.data.success) {
        setInstallationData(response.data.data);
        setTotalCount(response.data.totalRecords || 0);
        setTotalPages(response.data.totalPages);
      } else {
        throw new Error(response.data.message || "Failed to fetch installation data");
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.message || "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [selectedState, empData?.department, currentPage, limit, debouncedSearchInput, selectedRemarks]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchInput(searchInput), 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset currentPage to 1 when any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedState, selectedRemarks, searchInput]);

  // Fetch when dependencies change
  useEffect(() => {
    fetchInstallationData();
  }, [fetchInstallationData]);

  // Scroll to top button visibility (window scroll)
  useEffect(() => {
    const handleWindowScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleWindowScroll);
    return () => window.removeEventListener("scroll", handleWindowScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const openImage = (imageUrl, fieldName, saralId, date) => {
    setSelectedImage({
      url: getFullImageUrl(imageUrl),
      fieldName,
      saralId,
      date,
    });
    document.body.style.overflow = "hidden";
  };

  const closeImage = () => {
    setSelectedImage({ url: null, fieldName: null, saralId: null, date: null });
    document.body.style.overflow = "auto";
  };

  const downloadImage = async (imageUrl, fieldName, saralId, date) => {
    try {
      const fullUrl = getFullImageUrl(imageUrl);
      const response = await fetch(fullUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      const formattedDate = date ? new Date(date).toISOString().split("T")[0] : new Date().toISOString().split("T")[0];
      link.download = `${saralId || "installation"}_${fieldName || "photo"}_${formattedDate}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading image:", error);
      alert("Failed to download image. Please try again.");
    }
  };

  const downloadPdf = async (installation) => {
    try {
      setDownloadingPdf(installation._id);
      const photoUrls = [
        ...(installation.finalFoundationFarmerPhoto || []),
        ...(installation.panelFarmerPhoto || []),
        ...(installation.controllerBoxFarmerPhoto || []),
        ...(installation.waterDischargeFarmerPhoto || []),
      ].filter((url) => url);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL_API_URL}/api/external/downloadInstallationPDF?farmerSaralId=${installation.farmerSaralId}`,
        { photoUrls },
        { responseType: "blob", timeout: 60000 }
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${installation.farmerSaralId || "installation"}_${formatDate(installation.createdAt)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Failed to download PDF. Please try again.");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const downloadExcel = async () => {
    try {
      setDownloadingExcel(true);
      const params = new URLSearchParams();
      if (selectedState) params.append("state", selectedState);
      if (selectedRemarks) params.append("remarks", selectedRemarks);
      if (searchInput.trim()) params.append("search", searchInput.trim()); // also use "search" for Excel export
      const department = empData?.department || "";
      if (department) params.append("department", department);
      params.append("_t", Date.now());
      const url = `${process.env.REACT_APP_BASE_URL_API_URL}/common/installed-system-excel?${params.toString()}`;
      const response = await axios.get(url, { responseType: "blob", timeout: 60000 });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      const today = new Date().toISOString().split("T")[0];
      link.download = `installed_systems_${selectedState}_${today}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Error downloading Excel:", err);
      alert("Failed to download Excel file. Please try again.");
    } finally {
      setDownloadingExcel(false);
    }
  };

  // Bulk Approve via Excel Upload
  const handleExcelFileSelect = (event) => {
    const file = event.target.files[0];
    if (
      file &&
      (file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel")
    ) {
      setSelectedExcelFile(file);
    } else {
      alert("Please select a valid Excel file (.xlsx or .xls)");
      event.target.value = "";
    }
  };

  const uploadAndBulkApprove = async () => {
    if (!selectedExcelFile) {
      alert("Please select an Excel file first");
      return;
    }
    setUploadingExcel(true);
    setBulkApproveResult(null);
    try {
      const formData = new FormData();
      formData.append("file", selectedExcelFile);
      formData.append("updatedByName", empData?.name || "");
      formData.append("updatedByEmpId", empData?._id || "");
      formData.append("department", empData?.department || "");
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL_API_URL}/service-team/approve-installations/excel`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
          timeout: 120000,
        }
      );
      const contentType = response.headers["content-type"];
      if (contentType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
        link.download = `bulk_approval_errors_${timestamp}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        alert("Bulk approval completed with errors. Download the error report for details.");
        setExcelUploadModal(false);
        setSelectedExcelFile(null);
        return;
      }
      const text = await response.data.text();
      const jsonResponse = JSON.parse(text);
      if (jsonResponse.success) {
        setBulkApproveResult({
          success: true,
          message: jsonResponse.message,
          data: jsonResponse.data,
        });
        await fetchInstallationData();
        setExcelUploadModal(false);
        setSelectedExcelFile(null);
        alert(`Successfully approved ${jsonResponse?.data?.approvedCount || 0} installations`);
      } else {
        throw new Error(jsonResponse.message || "Failed to bulk approve");
      }
    } catch (err) {
      console.error("Error in bulk approval:", err);
      if (
        err.response &&
        err.response.headers &&
        err.response.headers["content-type"] === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        const blob = new Blob([err.response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
        link.download = `bulk_approval_errors_${timestamp}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        alert("Bulk approval failed. Download the error report for details.");
      } else {
        const errorMsg = err.response?.data?.message || err.message;
        setBulkApproveResult({
          success: false,
          message: errorMsg,
          errors: err.response?.data?.errors,
        });
        alert(`Error: ${errorMsg}`);
      }
    } finally {
      setUploadingExcel(false);
    }
  };

  const handleEditClick = (installation) => {
    setEditingId(installation._id);
    setEditFormData({
      latitude: installation.latitude || "",
      longitude: installation.longitude || "",
      remarks: installation.remarks || "",
    });
    setImageUploads({});
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditFormData({ latitude: "", longitude: "", remarks: "" });
    setImageUploads({});
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e, fieldName) => {
    const files = Array.from(e.target.files);
    const maxSize = fieldName === "installationVideo" ? 20 * 1024 * 1024 : 5 * 1024 * 1024;
    const validFiles = files.filter((file) => file.size <= maxSize);
    if (validFiles.length !== files.length) {
      alert(`Some files exceed ${maxSize / (1024 * 1024)}MB limit and were skipped`);
    }
    setImageUploads((prev) => ({ ...prev, [fieldName]: validFiles }));
  };

  const handleUpdateInstallation = async (installationId) => {
    if (editFormData.latitude) {
      const lat = parseFloat(editFormData.latitude);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        alert("Invalid latitude. Must be between -90 and 90");
        return;
      }
    }
    if (editFormData.longitude) {
      const lng = parseFloat(editFormData.longitude);
      if (isNaN(lng) || lng < -180 || lng > 180) {
        alert("Invalid longitude. Must be between -180 and 180");
        return;
      }
    }
    try {
      const formData = new FormData();
      formData.append("latitude", editFormData.latitude);
      formData.append("longitude", editFormData.longitude);
      formData.append("remarks", editFormData.remarks);
      formData.append("installationId", installationId);
      formData.append("empName", empData?.name || "");
      Object.keys(imageUploads).forEach((fieldName) => {
        imageUploads[fieldName].forEach((file) => {
          formData.append(`${fieldName}`, file);
        });
      });
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL_API_URL}/service-team/update-installation-data`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        }
      );
      if (response.data.success) {
        await fetchInstallationData();
        setEditingId(null);
        setEditFormData({ latitude: "", longitude: "", remarks: "" });
        setImageUploads({});
        alert("Installation updated successfully!");
      } else {
        throw new Error(response.data.message || "Failed to update installation");
      }
    } catch (err) {
      console.error("Error updating installation:", err);
      alert(`Error: ${err.message}`);
    }
  };

  const handleStateChange = (e) => {
    setSelectedState(e.target.value);
    setSearchInput("");
    setSelectedRemarks("");
  };

  const handleApprove = async (installationId) => {
    const confirmed = window.confirm(
      "Are you sure you want to approve this installation?\n\n" +
        "This action will mark the installation as approved by VT-2 and cannot be easily undone."
    );
    if (confirmed) {
      setApprovingId(installationId);
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL_API_URL}/service-team/approve-installation-data`,
          {
            installationId,
            updatedByName: empData?.name,
            updatedByEmpId: empData?._id,
            department: empData?.department,
          },
          { timeout: 30000 }
        );
        if (response.data.success) {
          await fetchInstallationData();
          alert("Installation approved successfully");
        } else {
          throw new Error(response.data.message || "Failed to approve");
        }
      } catch (err) {
        console.error("Error approving installation:", err);
        alert(`Error: ${err.message}`);
      } finally {
        setApprovingId(null);
      }
    }
  };

  const categoryToFieldMap = {
    pit: "pitPhoto",
    earthing: "earthingFarmerPhoto",
    antiTheft: "antiTheftNutBoltPhoto",
    lightning: "lightingArresterInstallationPhoto",
    finalFoundation: "finalFoundationFarmerPhoto",
    panel: "panelFarmerPhoto",
    controller: "controllerBoxFarmerPhoto",
    waterDischarge: "waterDischargeFarmerPhoto",
    installationVideo: "installationVideo",
  };

  const openDeclineModal = (installation) => {
    const allPhotos = [];
    const pushPhotos = (arr, category) => {
      if (arr && arr.length) {
        arr.forEach((url, idx) => {
          allPhotos.push({ url, category, index: idx, id: `${category}-${idx}` });
        });
      }
    };
    pushPhotos(installation.pitPhoto, "pit");
    pushPhotos(installation.earthingFarmerPhoto, "earthing");
    pushPhotos(installation.antiTheftNutBoltPhoto, "antiTheft");
    pushPhotos(installation.lightingArresterInstallationPhoto, "lightning");
    pushPhotos(installation.finalFoundationFarmerPhoto, "finalFoundation");
    pushPhotos(installation.panelFarmerPhoto, "panel");
    pushPhotos(installation.controllerBoxFarmerPhoto, "controller");
    pushPhotos(installation.waterDischargeFarmerPhoto, "waterDischarge");
    pushPhotos(installation.installationVideo, "installationVideo");
    setDeclineModal({
      open: true,
      installationId: installation._id,
      photos: allPhotos,
    });
    setSelectedPhotos([]);
    document.body.style.overflow = "hidden";
  };

  const closeDeclineModal = () => {
    setDeclineModal({ open: false, installationId: null, photos: [] });
    setSelectedPhotos([]);
    document.body.style.overflow = "auto";
  };

  const togglePhotoSelection = (photo) => {
    setSelectedPhotos((prev) => {
      const exists = prev.some((p) => p.url === photo.url);
      if (exists) return prev.filter((p) => p.url !== photo.url);
      else return [...prev, photo];
    });
  };

  const selectAllPhotos = () => setSelectedPhotos([...declineModal.photos]);
  const deselectAllPhotos = () => setSelectedPhotos([]);

  const submitDeclineWithPhotos = async () => {
    if (selectedPhotos.length === 0) {
      alert("Please select at least one photo or video to mark as not proper.");
      return;
    }
    const confirmed = window.confirm(
      `Are you sure you want to decline this installation?\n\n` +
        `You have selected ${selectedPhotos.length} media file(s) to mark as "Not Proper".\n` +
        `This action cannot be undone.`
    );
    if (!confirmed) return;
    const rejectedFiles = selectedPhotos.map((photo) => ({
      type: categoryToFieldMap[photo.category] || photo.category,
      url: getFullImageUrl(photo.url),
    }));
    setDecliningId(declineModal.installationId);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL_API_URL}/service-team/delete-rejected-photos`,
        {
          installationId: declineModal.installationId,
          rejectedFiles,
          updatedByName: empData?.name,
          updatedByEmpId: empData?._id,
        },
        { timeout: 30000 }
      );
      if (response.data.success) {
        await fetchInstallationData();
        alert("Installation declined with selected media marked as not proper.");
        closeDeclineModal();
      } else {
        throw new Error(response.data.message || "Failed to decline");
      }
    } catch (err) {
      console.error("Error declining installation:", err);
      alert(`Error: ${err.message}`);
    } finally {
      setDecliningId(null);
    }
  };

  const PhotoGallery = ({
    photos,
    title,
    fieldName,
    installation,
    editingId,
    handleImageUpload,
    openImage,
  }) => {
    if (!photos || photos.length === 0) return null;
    return (
      <div className="border rounded p-2">
        <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
          <h4 className="font-medium text-xs sm:text-sm">{title}</h4>
          {editingId === installation._id && !isApprovedByVT2(installation) && (
            <input
              type="file"
              accept={fieldName === "installationVideo" ? "video/*" : "image/*"}
              multiple
              onChange={(e) => handleImageUpload(e, fieldName)}
              className="text-xs w-20 sm:w-24"
            />
          )}
        </div>
        <div className="flex flex-row flex-nowrap gap-2 overflow-x-auto pb-1">
          {photos.slice(0, 4).map((media, index) =>
            fieldName === "installationVideo" ? (
              <video
                key={`${fieldName}-${index}`}
                src={getFullImageUrl(media)}
                controls
                className="w-24 h-20 sm:w-32 sm:h-24 md:w-40 md:h-32 object-cover rounded flex-shrink-0 cursor-pointer"
                onClick={() =>
                  openImage(media, title, installation.farmerSaralId, installation.createdAt)
                }
              />
            ) : (
              <img
                key={`${fieldName}-${index}`}
                src={getFullImageUrl(media)}
                alt={`${title} ${index + 1}`}
                className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-cover rounded cursor-pointer hover:scale-105 transition flex-shrink-0"
                onClick={() =>
                  openImage(media, title, installation.farmerSaralId, installation.createdAt)
                }
              />
            )
          )}
          {photos.length > 4 && (
            <div className="flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded">
              <span className="text-xs text-gray-600">+{photos.length - 4} more</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const goToPrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  if (loading && installationData.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-screen text-xl">
        <FaSpinner className="animate-spin text-4xl mb-4 text-blue-600" />
        <p>Loading installation data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-screen text-xl text-red-600">
        <p className="mb-4">Error: {error}</p>
        <button
          onClick={fetchInstallationData}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-3 sm:p-4 md:p-6">
      {/* Image Modal */}
      {selectedImage.url && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-2 sm:p-4"
          onClick={closeImage}
        >
          <div
            className="relative max-w-5xl w-full bg-white rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeImage}
              className="absolute top-2 right-2 text-2xl sm:text-3xl text-gray-600 hover:text-gray-900 z-10 bg-white rounded-full w-8 h-8 flex items-center justify-center"
            >
              &times;
            </button>
            <img
              src={selectedImage.url}
              alt="Full screen"
              className="w-full h-auto max-h-[80vh] object-contain"
            />
            <div className="flex flex-col sm:flex-row justify-end gap-2 p-3 bg-gray-100">
              <button
                onClick={() =>
                  downloadImage(
                    selectedImage.url,
                    selectedImage.fieldName,
                    selectedImage.saralId,
                    selectedImage.date
                  )
                }
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center gap-1 text-sm sm:text-base"
              >
                <FaDownload /> Download
              </button>
              <button
                onClick={closeImage}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center justify-center gap-1 text-sm sm:text-base"
              >
                <FaTimes /> Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Excel Upload Modal for Bulk Approval */}
      {excelUploadModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-2 sm:p-4"
          onClick={() => setExcelUploadModal(false)}
        >
          <div
            className="relative max-w-2xl w-full bg-white rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-3 sm:p-4 border-b flex justify-between items-center">
              <h2 className="text-lg sm:text-xl font-semibold">
                Bulk Approve Installations via Excel
              </h2>
              <button
                onClick={() => setExcelUploadModal(false)}
                className="text-2xl text-gray-600 hover:text-gray-900"
              >
                &times;
              </button>
            </div>
            <div className="p-3 sm:p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Excel File with Saral IDs
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Excel file should have a column named "saralId" containing the Saral IDs to approve
                </p>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleExcelFileSelect}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {selectedExcelFile && (
                  <p className="text-sm text-green-600 mt-1">
                    Selected: {selectedExcelFile.name}
                  </p>
                )}
              </div>
              {bulkApproveResult && !bulkApproveResult.success && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm text-red-700 font-semibold">Error:</p>
                  <p className="text-sm text-red-600">{bulkApproveResult.message}</p>
                  {bulkApproveResult.errors && (
                    <div className="mt-2">
                      <p className="text-xs text-red-600 font-semibold">Details:</p>
                      <ul className="list-disc list-inside text-xs text-red-600">
                        {bulkApproveResult.errors.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="p-3 sm:p-4 border-t flex flex-col sm:flex-row justify-end gap-2">
              <button
                onClick={() => {
                  setExcelUploadModal(false);
                  setSelectedExcelFile(null);
                  setBulkApproveResult(null);
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center justify-center gap-1 text-sm sm:text-base"
              >
                <FaTimes /> Cancel
              </button>
              <button
                onClick={uploadAndBulkApprove}
                disabled={!selectedExcelFile || uploadingExcel}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-1 text-sm sm:text-base"
              >
                {uploadingExcel ? (
                  <>
                    <FaSpinner className="animate-spin" /> Processing...
                  </>
                ) : (
                  <>
                    <FaUpload /> Upload & Approve
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Decline Modal */}
      {declineModal.open && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-2 sm:p-4"
          onClick={closeDeclineModal}
        >
          <div
            className="relative max-w-4xl w-full bg-white rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-3 sm:p-4 border-b flex justify-between items-center flex-wrap gap-2">
              <h2 className="text-lg sm:text-xl font-semibold">
                Select Photos/Videos to Mark as "Not Proper"
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={selectAllPhotos}
                  className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                >
                  Select All
                </button>
                <button
                  onClick={deselectAllPhotos}
                  className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                >
                  Deselect All
                </button>
                <button
                  onClick={closeDeclineModal}
                  className="text-2xl text-gray-600 hover:text-gray-900"
                >
                  &times;
                </button>
              </div>
            </div>
            <div className="p-3 sm:p-4 max-h-[60vh] overflow-y-auto">
              {declineModal.photos.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No photos or videos available for this installation.
                </p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4">
                  {declineModal.photos.map((photo) => (
                    <div
                      key={photo.id}
                      className="relative border rounded p-1 sm:p-2 hover:shadow-lg transition"
                    >
                      {photo.category === "installationVideo" ? (
                        <video
                          src={getFullImageUrl(photo.url)}
                          controls
                          className="w-full h-20 sm:h-24 object-cover rounded"
                        />
                      ) : (
                        <img
                          src={getFullImageUrl(photo.url)}
                          alt={photo.category}
                          className="w-full h-20 sm:h-24 object-cover rounded"
                        />
                      )}
                      <div className="absolute top-1 left-1">
                        <input
                          type="checkbox"
                          checked={selectedPhotos.some((p) => p.url === photo.url)}
                          onChange={() => togglePhotoSelection(photo)}
                          className="w-4 h-4 sm:w-5 sm:h-5 cursor-pointer"
                        />
                      </div>
                      <p className="text-xs mt-1 text-gray-600 truncate">{photo.category}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-3 sm:p-4 border-t flex flex-col sm:flex-row justify-end gap-2">
              <button
                onClick={closeDeclineModal}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center justify-center gap-1 text-sm sm:text-base"
              >
                <FaTimes /> Cancel
              </button>
              <button
                onClick={submitDeclineWithPhotos}
                disabled={decliningId === declineModal.installationId || selectedPhotos.length === 0}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-1 text-sm sm:text-base"
              >
                {decliningId === declineModal.installationId ? (
                  <>
                    <FaSpinner className="animate-spin" /> Processing...
                  </>
                ) : (
                  <>
                    <FaBan /> Mark Selected as Not Proper ({selectedPhotos.length})
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header & Filters */}
      <div className="mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-3 sm:mb-4">
          COMPLETE INSTALLATION DATA - {selectedState.toUpperCase()}
        </h1>

        <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 items-stretch lg:items-center justify-between">
          <div className="flex flex-col xs:flex-row items-stretch xs:items-center gap-2 w-full lg:w-auto">
            <label htmlFor="state-select" className="font-medium text-sm sm:text-base whitespace-nowrap">
              Filter by State:
            </label>
            <select
              id="state-select"
              value={selectedState}
              onChange={handleStateChange}
              className="border rounded px-3 py-2 bg-white w-full xs:w-auto sm:w-48 text-sm sm:text-base"
            >
              {states.map((state, index) => (
                <option key={index} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col xs:flex-row items-stretch xs:items-center gap-2 w-full lg:w-auto">
            <label className="font-medium text-sm sm:text-base whitespace-nowrap flex items-center gap-1">
              <FaFilter className="text-gray-500" /> Filter by Remarks:
            </label>
            <select
              value={selectedRemarks}
              onChange={(e) => setSelectedRemarks(e.target.value)}
              className="border rounded px-3 py-2 bg-white w-full xs:w-auto sm:w-56 text-sm sm:text-base"
            >
              <option value="">All Remarks</option>
              {filterData.map((item, index) => (
                <option key={index} value={item.remarks}>
                  {item.remarks}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col xs:flex-row items-stretch xs:items-center gap-2 w-full lg:w-auto">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by Saral ID..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="border rounded px-3 py-2 pl-9 w-full xs:w-64 text-sm sm:text-base"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={downloadExcel}
              disabled={downloadingExcel}
              className="px-4 py-2 bg-green-700 text-white rounded text-sm sm:text-base flex items-center justify-center gap-2 hover:bg-green-800 transition disabled:opacity-50"
            >
              {downloadingExcel ? (
                <>
                  <FaSpinner className="animate-spin" /> Exporting...
                </>
              ) : (
                <>
                  <FaFileExcel className="text-lg" /> Export to Excel
                </>
              )}
            </button>
            <button
              onClick={() => setExcelUploadModal(true)}
              disabled={uploadingExcel}
              className="px-4 py-2 bg-blue-700 text-white rounded text-sm sm:text-base flex items-center justify-center gap-2 hover:bg-blue-800 transition"
            >
              <FaUpload className="text-lg" /> Bulk Approve via Excel
            </button>
          </div>
        </div>

        {/* Total records count */}
        <div className="mt-3 text-sm text-gray-700">
          <span className="font-semibold">Total records :</span> {totalCount}
        </div>
      </div>

      {/* Installations List */}
      <div className="space-y-4">
        {installationData.length > 0 ? (
          installationData.map((installation) => {
            const rejected = isRejectedByVT1(installation);
            const approved = isApprovedByVT2(installation);
            const displayStatus = getDisplayStatus(installation);
            const statusColor = getStatusColor(displayStatus);
            return (
              <div
                key={installation._id}
                className="bg-white rounded-lg shadow p-3 sm:p-4 hover:shadow-lg transition"
              >
                {/* Row Header */}
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 border-b pb-3 mb-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm sm:text-base">
                    <h3 className="font-semibold text-base sm:text-lg">
                      Saral ID: {installation.farmerSaralId || "N/A"}
                    </h3>
                    <span className="text-xs sm:text-sm text-gray-500">
                      {formatDate(installation.createdAt)}
                    </span>
                    {installation.farmerDetails?.state && (
                      <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                        {installation.farmerDetails.state}
                      </span>
                    )}
                    {displayStatus && (
                      <span
                        className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${statusColor}`}
                      >
                        {displayStatus === "Approved By VT-2" && <FaCheck size={12} />}
                        {(displayStatus === "Rejected By VT-2" || rejected) && <FaBan size={12} />}
                        {displayStatus}
                      </span>
                    )}
                  </div>

                  {editingId === installation._id ? (
                    <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
                      <div className="flex items-center gap-1">
                        <span className="text-xs sm:text-sm font-medium">Lat:</span>
                        <input
                          type="text"
                          name="latitude"
                          value={editFormData.latitude}
                          onChange={handleEditFormChange}
                          className="border rounded px-2 py-1 w-20 sm:w-24 text-sm"
                          placeholder="Latitude"
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-xs sm:text-sm font-medium">Lng:</span>
                        <input
                          type="text"
                          name="longitude"
                          value={editFormData.longitude}
                          onChange={handleEditFormChange}
                          className="border rounded px-2 py-1 w-20 sm:w-24 text-sm"
                          placeholder="Longitude"
                        />
                      </div>
                      {!approved && (
                        <div className="flex items-center gap-1 min-w-[150px]">
                          <span className="text-xs sm:text-sm font-medium">Remarks:</span>
                          <select
                            name="remarks"
                            value={editFormData.remarks}
                            onChange={handleEditFormChange}
                            className="border rounded px-2 py-1 text-sm flex-1"
                          >
                            <option value="">Select Remarks</option>
                            {filterData.map((item, index) => (
                              <option key={index} value={item.remarks}>
                                {item.remarks}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleUpdateInstallation(installation._id)}
                          className="px-3 py-1 bg-green-600 text-white rounded text-xs sm:text-sm hover:bg-green-700 flex items-center gap-1"
                        >
                          <FaSave /> Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-3 py-1 bg-gray-500 text-white rounded text-xs sm:text-sm hover:bg-gray-600 flex items-center gap-1"
                        >
                          <FaTimes /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col xs:flex-row xs:flex-wrap items-start xs:items-center gap-3 w-full lg:w-auto">
                      {!approved && (
                        <span>
                          <span className="font-medium">Remarks:</span>{" "}
                          <span
                            className={
                              installation.remarks ? "text-orange-600 font-medium" : "text-gray-500"
                            }
                          >
                            {installation.remarks || "N/A"}
                          </span>
                        </span>
                      )}
                      <div className="flex gap-2 text-xs sm:text-sm">
                        <span>
                          <span className="font-medium">Lat:</span> {installation.latitude || "N/A"}
                        </span>
                        <span>
                          <span className="font-medium">Lng:</span> {installation.longitude || "N/A"}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 w-full xs:w-auto">
                        {!approved && (
                          <button
                            onClick={() => handleEditClick(installation)}
                            disabled={rejected}
                            className={`px-3 py-1 bg-yellow-500 text-white rounded text-xs sm:text-sm flex items-center gap-1 ${
                              rejected ? "opacity-50 cursor-not-allowed" : "hover:bg-yellow-600"
                            } flex-1 xs:flex-none`}
                          >
                            <FaEdit /> Edit
                          </button>
                        )}
                        <button
                          onClick={() => downloadPdf(installation)}
                          disabled={downloadingPdf === installation._id || rejected}
                          className={`px-3 py-1 bg-purple-600 text-white rounded text-xs sm:text-sm flex items-center gap-1 ${
                            downloadingPdf === installation._id || rejected
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:bg-purple-700"
                          } flex-1 xs:flex-none`}
                        >
                          {downloadingPdf === installation._id ? (
                            <>
                              <FaSpinner className="animate-spin" /> Downloading...
                            </>
                          ) : (
                            <>
                              <FaFilePdf /> PDF
                            </>
                          )}
                        </button>
                        {!approved && (
                          <button
                            onClick={() => handleApprove(installation._id)}
                            disabled={
                              approvingId === installation._id ||
                              decliningId === installation._id ||
                              rejected
                            }
                            className={`px-3 py-1 bg-green-600 text-white rounded text-xs sm:text-sm flex items-center gap-1 ${
                              approvingId === installation._id ||
                              decliningId === installation._id ||
                              rejected
                                ? "opacity-50 cursor-not-allowed"
                                : "hover:bg-green-700"
                            } flex-1 xs:flex-none`}
                          >
                            {approvingId === installation._id ? (
                              <>
                                <FaSpinner className="animate-spin" /> Approving...
                              </>
                            ) : (
                              <>
                                <FaCheck /> Approve
                              </>
                            )}
                          </button>
                        )}
                        {!approved && (
                          <button
                            onClick={() => openDeclineModal(installation)}
                            disabled={
                              approvingId === installation._id ||
                              decliningId === installation._id ||
                              rejected
                            }
                            className={`px-3 py-1 bg-red-600 text-white rounded text-xs sm:text-sm flex items-center gap-1 ${
                              approvingId === installation._id ||
                              decliningId === installation._id ||
                              rejected
                                ? "opacity-50 cursor-not-allowed"
                                : "hover:bg-red-700"
                            } flex-1 xs:flex-none`}
                          >
                            {decliningId === installation._id ? (
                              <>
                                <FaSpinner className="animate-spin" /> Declining...
                              </>
                            ) : (
                              <>
                                <FaTimes /> Decline
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Photo Sections Grid */}
                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  <PhotoGallery
                    photos={installation.pitPhoto}
                    title="Pit Photo"
                    fieldName="pitPhoto"
                    installation={installation}
                    editingId={editingId}
                    handleImageUpload={handleImageUpload}
                    openImage={openImage}
                  />
                  <PhotoGallery
                    photos={installation.earthingFarmerPhoto}
                    title="Earthing"
                    fieldName="earthingFarmerPhoto"
                    installation={installation}
                    editingId={editingId}
                    handleImageUpload={handleImageUpload}
                    openImage={openImage}
                  />
                  <PhotoGallery
                    photos={installation.antiTheftNutBoltPhoto}
                    title="Anti Theft"
                    fieldName="antiTheftNutBoltPhoto"
                    installation={installation}
                    editingId={editingId}
                    handleImageUpload={handleImageUpload}
                    openImage={openImage}
                  />
                  <PhotoGallery
                    photos={installation.lightingArresterInstallationPhoto}
                    title="Lightning"
                    fieldName="lightingArresterInstallationPhoto"
                    installation={installation}
                    editingId={editingId}
                    handleImageUpload={handleImageUpload}
                    openImage={openImage}
                  />
                  <PhotoGallery
                    photos={installation.finalFoundationFarmerPhoto}
                    title="Foundation"
                    fieldName="finalFoundationFarmerPhoto"
                    installation={installation}
                    editingId={editingId}
                    handleImageUpload={handleImageUpload}
                    openImage={openImage}
                  />
                  <PhotoGallery
                    photos={installation.panelFarmerPhoto}
                    title="Panel"
                    fieldName="panelFarmerPhoto"
                    installation={installation}
                    editingId={editingId}
                    handleImageUpload={handleImageUpload}
                    openImage={openImage}
                  />
                  <PhotoGallery
                    photos={installation.controllerBoxFarmerPhoto}
                    title="Controller"
                    fieldName="controllerBoxFarmerPhoto"
                    installation={installation}
                    editingId={editingId}
                    handleImageUpload={handleImageUpload}
                    openImage={openImage}
                  />
                  <PhotoGallery
                    photos={installation.waterDischargeFarmerPhoto}
                    title="Water Discharge"
                    fieldName="waterDischargeFarmerPhoto"
                    installation={installation}
                    editingId={editingId}
                    handleImageUpload={handleImageUpload}
                    openImage={openImage}
                  />
                  <PhotoGallery
                    photos={installation.installationVideo}
                    title="Installation Video"
                    fieldName="installationVideo"
                    installation={installation}
                    editingId={editingId}
                    handleImageUpload={handleImageUpload}
                    openImage={openImage}
                  />
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-10 text-gray-500 bg-white rounded-lg">
            <p className="text-lg">No installations found</p>
            {(searchInput || selectedRemarks) && (
              <p className="text-sm mt-2">
                {searchInput && `Matching "${searchInput}" `}
                {selectedRemarks && `with remarks "${selectedRemarks}" `}
                {selectedState && `in ${selectedState}`}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-3 mt-6">
          <button
            onClick={goToPrevPage}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50 hover:bg-blue-700 flex items-center gap-1"
          >
            <FaChevronLeft size={14} /> Previous
          </button>
          <span className="text-sm font-medium">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50 hover:bg-blue-700 flex items-center gap-1"
          >
            Next <FaChevronRight size={14} />
          </button>
        </div>
      )}

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 bg-blue-600 text-white w-10 h-10 sm:w-12 sm:h-12 rounded-full shadow-lg flex items-center justify-center text-xl sm:text-2xl hover:bg-blue-700 transition z-40"
          aria-label="Scroll to top"
        >
          <FaArrowUp />
        </button>
      )}
    </div>
  );
};

export default CompleteInstallationMaharastra;